import { Avatar } from "@dropbox/dig-components/avatar";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { getInitials } from "@mirage/shared/account";
import { getSharingUserContacts } from "@mirage/stacks/ShareModal/Api";
import i18n from "@mirage/translations";
import cx from "classnames";
import { forwardRef, useEffect, useState } from "react";

import type { AvatarSizes } from "@dropbox/dig-components/avatar";
import styles from "./UserProfileAvatar.module.css";

type ProfileAvatarProps = {
  user: {
    name: string;
    email: string;
    profile_photo_url: string | null;
    on_leave?: boolean;
    is_deactivated?: boolean;
  };
  size?: AvatarSizes | "profile" | "customize_large";
  className?: string;
  style?: React.CSSProperties;
};

/**
 * Avatar component for displaying user a profile photo or initials
 *
 * @param user - user info
 * @param size - size of avatar
 * @param className - custom className for avatar
 * @param style - custom style for avatar
 */
export const UserProfileAvatar = forwardRef<HTMLElement, ProfileAvatarProps>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ({ user, size, className, style }, ref: any) => {
    const userPhotoUrl = user.profile_photo_url;
    const hasPhotoUrl = !!userPhotoUrl?.length;
    const [imageLoaded, setImageLoaded] = useState(hasPhotoUrl);

    // If user does not already have a profile photo via user.profile_photo_url,
    // we will need to fetch one and render it via fetchedPhotoUrl
    const [fetchedPhotoUrl, setFetchedPhotoUrl] = useState<string>("");

    const isCustomSize = ["profile", "customize_large"].includes(size ?? "");

    const employeeInitials = getInitials(user.name);

    useEffect(() => {
      // Reset avatar state whenever user changes
      setFetchedPhotoUrl("");
      setImageLoaded(hasPhotoUrl);

      const fetchUserContacts = async () => {
        const result = await getSharingUserContacts(user.email);
        if (result?.[0]?.profilePhotoUrl) {
          setFetchedPhotoUrl(result[0].profilePhotoUrl);
        } else {
          setImageLoaded(true);
        }
      };

      if (!hasPhotoUrl) {
        fetchUserContacts();
      }
    }, [user.email]);

    // Hacky workaround until we allow people to adjust their crop
    useEffect(() => {
      if (hasPhotoUrl) {
        return;
      }

      const image = new Image();
      image.src = fetchedPhotoUrl;

      const onLoad = () => {
        setImageLoaded(true);
      };

      image.addEventListener("load", onLoad);
      return () => {
        image.removeEventListener("load", onLoad);
      };
    }, [fetchedPhotoUrl]);

    return (
      <Box
        position="relative"
        ref={ref}
        className={cx(className, {
          [styles.avatar48]: size === "customize_large",
          [styles.avatar116]: size === "profile",
        })}
        style={style}
      >
        {user.on_leave && imageLoaded && (
          <div className={styles.wrapper}>
            <div className={styles.ticker}>
              <div className={styles.tickerText}>
                <Text size="small">{i18n.t("currently_on_leave")}</Text>
              </div>
            </div>
          </div>
        )}
        <Box
          as={Avatar}
          colorPalette={
            imageLoaded
              ? undefined
              : {
                  background: "var(--dig-color__opacity__surface)",
                  foreground: "",
                }
          }
          src={hasPhotoUrl ? userPhotoUrl : fetchedPhotoUrl}
          size={isCustomSize ? "large" : (size as AvatarSizes)}
          alt={user.name}
          isInactive={user.is_deactivated}
          className={cx(styles.avatar, {
            [styles.deactivatedAvatarLoaded]: user.is_deactivated,
          })}
        >
          {imageLoaded ? employeeInitials : undefined}
        </Box>
      </Box>
    );
  },
);

export const UserProfileAvatarImage: React.FC<{
  displayName: string;
  profilePhotoUrl: string;
  size?: AvatarSizes;
}> = ({ displayName, profilePhotoUrl, size = "standard" }) => {
  const initials = getInitials(displayName);
  return (
    <Avatar
      alt={displayName}
      src={profilePhotoUrl}
      size={size}
      hasImageBackgroundColor={false}
      hasNoOutline
    >
      {initials}
    </Avatar>
  );
};

UserProfileAvatar.displayName = "UserProfileAvatar";
